import React, { useState, useEffect, useContext, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import IconButton from "@material-ui/core/IconButton";
import MoreVert from "@material-ui/icons/MoreVert";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import {
  FormControlLabel,
  Menu,
  MenuItem,
  Switch,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import { messengerText as text } from "../../translate/messenger";

let mediaRecorder = null;
let audioChunks = [];
let audioStream = null;

const startRecorder = async () => {
  audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  audioChunks = [];

  const options = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
    ? { mimeType: "audio/webm;codecs=opus" }
    : undefined;

  mediaRecorder = new MediaRecorder(audioStream, options);
  mediaRecorder.addEventListener("dataavailable", event => {
    if (event.data.size > 0) audioChunks.push(event.data);
  });
  mediaRecorder.start();
};

const stopRecorder = () =>
  new Promise((resolve, reject) => {
    if (!mediaRecorder || mediaRecorder.state === "inactive") {
      resolve(null);
      return;
    }

    mediaRecorder.addEventListener(
      "stop",
      () => {
        const blob = new Blob(audioChunks, {
          type: mediaRecorder.mimeType || "audio/webm",
        });
        audioStream?.getTracks().forEach(track => track.stop());
        mediaRecorder = null;
        audioStream = null;
        audioChunks = [];
        resolve(blob);
      },
      { once: true }
    );
    mediaRecorder.addEventListener("error", reject, { once: true });
    mediaRecorder.stop();
  });

const useStyles = makeStyles(() => ({
  sendMessageIcons: {
    color: "grey",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: "#ffffff",
    padding: "2px",
    border: "1px solid #CCC",
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        "&:hover": {
          background: "#F1F1F1",
          cursor: "pointer",
        },
      },
    },
  },
}));

const MessageInput = ({ ticketStatus, ticketQueueId }) => {
  const classes = useStyles();
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);

  useEffect(() => {
    if (replyingMessage) inputRef.current?.focus();
  }, [replyingMessage]);

  useEffect(() => {
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  const handleChangeInput = e => {
    setInputMessage(e.target.value);
    handleLoadQuickAnswer(e.target.value);
  };

  const handleQuickAnswersClick = value => {
    setInputMessage(value);
    setTypeBar(false);
  };

  const handleAddEmoji = e => {
    let emoji = e.native;
    setInputMessage(prevState => prevState + emoji);
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handleInputPaste = e => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async e => {
    setLoading(true);
    e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach(media => {
      formData.append("medias", media);
      formData.append("body", media.name);
    });

    try {
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
    setMedias([]);
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: signMessage
        ? `*${user?.name}:*\n${inputMessage.trim()}`
        : inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    try {
      await api.post(`/messages/${ticketId}`, message);
    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      await startRecorder();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async value => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: {
            searchParam: inputMessage.substring(1),
            queueId: ticketQueueId || 0,
          },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const blob = await stopRecorder();
      if (!blob || blob.size < 1000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `${new Date().getTime()}.webm`;
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      await stopRecorder();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = () => {
    setAnchorEl(null);
  };

  const renderReplyingMessage = message => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  if (medias.length > 0)
    return (
      <Paper elevation={0} square className="tw-flex tw-w-full tw-shrink-0 tw-items-center tw-justify-between tw-gap-2 tw-bg-slate-100 tw-p-3 [&>span]:tw-min-w-0 [&>span]:tw-truncate dark:tw-bg-[#172b22]">
        <IconButton
          aria-label="cancel-upload"
          component="span"
          onClick={() => setMedias([])}
        >
          <CancelIcon className={classes.sendMessageIcons} />
        </IconButton>

        {loading ? (
          <div>
            <CircularProgress className={classes.circleLoading} />
          </div>
        ) : (
          <span>
            {medias[0]?.name}
            {/* <img src={media.preview} alt=""></img> */}
          </span>
        )}
        <IconButton
          aria-label="send-upload"
          component="span"
          onClick={handleUploadMedia}
          disabled={loading}
        >
          <SendIcon className="tw-text-messenger-green" />
        </IconButton>
      </Paper>
    );
  else {
    return (
      <Paper square elevation={0} className="tw-relative tw-flex tw-w-full tw-shrink-0 tw-flex-col tw-border-0 tw-border-t tw-border-solid tw-border-slate-100 tw-bg-[#eef5ef] tw-pb-[env(safe-area-inset-bottom)] dark:tw-border-slate-700 dark:tw-bg-[#172b22]">
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        <div className="tw-flex tw-w-full tw-items-end tw-gap-0.5 tw-p-2 sm:tw-gap-1 sm:tw-p-3">
          <IconButton aria-label="emojiPicker" disabled={loading || recording || ticketStatus !== "open"} onClick={() => setShowEmoji(current => !current)}><MoodIcon className="tw-text-slate-500 dark:tw-text-slate-300" /></IconButton>
          {showEmoji && <ClickAwayListener onClickAway={() => setShowEmoji(false)}><div className="tw-absolute tw-bottom-full tw-left-0 tw-z-20 tw-max-w-full tw-overflow-x-auto"><Picker perLine={8} showPreview={false} showSkinTones={false} onSelect={handleAddEmoji} /></div></ClickAwayListener>}
          <input multiple type="file" id="messenger-upload" disabled={loading || recording || ticketStatus !== "open"} className="tw-hidden" onChange={handleChangeMedias} />
          <label htmlFor="messenger-upload" className="tw-hidden tw-shrink-0 sm:tw-block"><IconButton aria-label="upload" component="span" disabled={loading || recording || ticketStatus !== "open"}><AttachFileIcon className="tw-text-slate-500 dark:tw-text-slate-300" /></IconButton></label>
          <IconButton className="sm:tw-hidden" aria-label={text("options")} onClick={handleOpenMenuClick}><AttachFileIcon className="tw-text-slate-500 dark:tw-text-slate-300" /></IconButton>
          <IconButton className="tw-hidden sm:tw-inline-flex" aria-label={text("options")} onClick={handleOpenMenuClick}><MoreVert className="tw-text-slate-500 dark:tw-text-slate-300" /></IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuItemClick}>
            <MenuItem component="label" htmlFor="messenger-upload" disabled={loading || recording || ticketStatus !== "open"} onClick={handleMenuItemClick} className="tw-min-h-[48px] sm:tw-hidden"><AttachFileIcon /><span className="tw-ml-2">{text("attachment")}</span></MenuItem>
            <MenuItem>
            <FormControlLabel label={i18n.t("messagesInput.signMessage")} control={<Switch size="small" checked={signMessage} onChange={event => setSignMessage(event.target.checked)} color="primary" />} />
          </MenuItem></Menu>
          <div className="tw-ring-1 tw-ring-transparent focus-within:tw-ring-green-600/40 motion-safe:tw-transition-shadow tw-duration-200 tw-relative tw-mb-0.5 tw-flex tw-min-w-0 tw-flex-1 tw-rounded-[24px] tw-bg-white tw-px-3 tw-py-2 dark:tw-bg-[#233b2f]">
            <InputBase
              inputRef={inputRef}
              className="tw-min-w-0 tw-flex-1 tw-text-base sm:tw-text-sm"
              placeholder={
                ticketStatus === "open"
                  ? text("message")
                  : i18n.t("messagesInput.placeholderClosed")
              }
              multiline
              maxRows={5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={recording || loading || ticketStatus !== "open"}
              onPaste={e => {
                ticketStatus === "open" && handleInputPaste(e);
              }}
              onKeyPress={e => {
                if (loading || e.shiftKey) return;
                else if (e.key === "Enter") {
                  handleSendMessage();
                }
              }}
            />
            {typeBar ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {quickAnswers.map((value, index) => {
                  return (
                    <li
                      className={classes.messageQuickAnswersWrapperItem}
                      key={index}
                    >
                      <a onClick={() => handleQuickAnswersClick(value.message)}>
                        {`${value.shortcut} - ${value.message}`}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div></div>
            )}
          </div>
          {inputMessage ? (
            <IconButton
              aria-label="sendMessage"
              className="tw-bg-messenger-green tw-text-white hover:tw-bg-messenger-forest"
              onClick={handleSendMessage}
              disabled={loading}
            >
              <SendIcon />
            </IconButton>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <HighlightOffIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
              </IconButton>
            </div>
          ) : (
            <IconButton
              aria-label="showRecorder"
              className="tw-bg-messenger-green tw-text-white hover:tw-bg-messenger-forest"
              disabled={loading || ticketStatus !== "open"}
              onClick={handleStartRecording}
            >
              <MicIcon />
            </IconButton>
          )}
        </div>
      </Paper>
    );
  }
};

export default MessageInput;
